import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWallet } from '@/hooks/useData';
import { useAuthStore } from '@/stores';
import { RefreshCw, DollarSign, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

const typeLabels: Record<string, string> = {
  PLAN_RENEW: 'تجديد باقة',
  PLAN_SUBSCRIBE: 'اشتراك جديد',
  WALLET_DEPOSIT: 'شحن رصيد',
  DEPOSIT: 'إيداع رصيد',
  REFUND: 'استرداد مالي',
  SUSPEND: 'تعليق اشتراك',
  RESUME: 'إعادة تفعيل',
};

const WalletPage: React.FC = () => {
  const { t } = useTranslation();
  const partnerId = useAuthStore((s) => s.partnerId);
  const { data: walletRaw, isLoading, isFetching, error, refetch } = useWallet();

  const walletData = walletRaw as any;
  const balance = walletData?.balance ?? 0;
  const transactions: any[] = Array.isArray(walletData?.transactions) ? walletData.transactions : [];

  const now = new Date();
  const lastUpdated = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Determine transaction direction from amount or type
  const getDirection = (tx: any): 'credit' | 'debit' => {
    const amountVal = tx.transactionAmount?.value ?? tx.amount ?? 0;
    if (amountVal > 0) return 'credit';
    if (tx.direction) return tx.direction;
    if (tx.transactionType === 'Credit' || tx.type === 'credit') return 'credit';
    return 'debit';
  };

  const getAmount = (tx: any): number => {
    return Math.abs(tx.transactionAmount?.value ?? tx.amount ?? tx.value ?? 0);
  };

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>
      <style>{`
        @media (max-width: 576px) {
          .wallet-balance-card {
            flex-direction: column !important;
            align-items: flex-start !important;
            padding: 20px !important;
            gap: 12px !important;
          }
          .wallet-balance-text {
            font-size: 26px !important;
          }
          .wallet-balance-value-container {
            justify-content: flex-start !important;
          }
        }
      `}</style>

      {/* Page header */}
      <div
        style={{
          display: 'flex',
          
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ color: '#e2e8f0', fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            {t('wallet.title') || 'المحفظة المالية'}
          </h1>
          {partnerId && (
            <p style={{ color: '#64748b', fontSize: '13px' }}>
              Partner ID: {partnerId}
            </p>
          )}
        </div>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '8px',
            padding: '8px 14px',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          <RefreshCw size={14} style={isFetching ? { animation: 'spin 1s linear infinite' } : {}} />
          {t('common.refresh') || 'تحديث'}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            direction: 'rtl',
          }}
        >
          <span style={{ color: '#f87171', fontSize: '13px' }}>
            خطأ في جلب بيانات المحفظة. تأكد من الاتصال بـ admin.ftth.iq
          </span>
          <AlertCircle size={16} color="#f87171" />
        </div>
      )}

      {/* Balance Card */}
      <div
        className="wallet-balance-card"
        style={{
          background: 'linear-gradient(135deg, rgba(13,31,60,0.9), rgba(16,40,80,0.9))',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '28px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          direction: 'rtl',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #059669, #10b981)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <DollarSign size={30} color="#fff" />
        </div>

        <div style={{ flex: 1, textAlign: 'right', width: '100%' }}>
          <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '8px' }}>
            {t('wallet.currentBalance') || 'الرصيد الحالي'}
          </div>
          <div className="wallet-balance-value-container" style={{ display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'flex-end' }}>
            <span style={{ color: '#64748b', fontSize: '18px' }}>IQD</span>
            {isLoading ? (
              <span className="wallet-balance-text" style={{ color: '#475569', fontSize: '42px', fontWeight: 800 }}>...</span>
            ) : (
              <span className="wallet-balance-text" style={{ color: '#e2e8f0', fontSize: '42px', fontWeight: 800, letterSpacing: '-1px' }}>
                {balance.toLocaleString('ar-IQ')}
              </span>
            )}
          </div>
          <div style={{ color: '#475569', fontSize: '12px', marginTop: '6px' }}>
            آخر تحديث: {lastUpdated}
          </div>
        </div>
      </div>

      {/* Transactions table */}
      <div
        style={{
          background: 'rgba(13,31,60,0.7)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          overflowX: 'auto',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '15px', textAlign: 'right' }}>
            {t('wallet.transactionHistory') || 'سجل الحركات المالية'}
          </h3>
        </div>
        <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
              {['المعرّف', 'نوع العملية', 'المبلغ', 'طريقة الدفع', 'المرجع', 'التاريخ'].map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    color: '#64748b',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    جاري جلب بيانات المحفظة من admin.ftth.iq...
                  </div>
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  لا توجد حركات مالية مسجلة
                </td>
              </tr>
            ) : (
              transactions.map((tx: any, i: number) => {
                const dir = getDirection(tx);
                const amount = getAmount(tx);
                return (
                  <tr
                    key={tx.id || i}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <td data-label="المعرّف" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap', direction: 'ltr', textAlign: 'right' }}>
                      {tx.id || tx.transactionId || `TX-${i + 1}`}
                    </td>
                    <td data-label="نوع العملية" style={{ padding: '12px 16px', color: '#e2e8f0', fontSize: '13px' }}>
                      {typeLabels[tx.type] || tx.typeName || tx.type || tx.transactionType || '—'}
                    </td>
                    <td data-label="المبلغ" style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                        {dir === 'credit' ? (
                          <TrendingUp size={14} color="#34d399" />
                        ) : (
                          <TrendingDown size={14} color="#f87171" />
                        )}
                        <span
                          style={{
                            color: dir === 'credit' ? '#34d399' : '#f87171',
                            fontWeight: 700,
                            fontSize: '14px',
                          }}
                        >
                          {dir === 'credit' ? '+' : '-'} {amount.toLocaleString('ar-IQ')} IQD
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>
                      {tx.paymentMethod?.displayValue || tx.paymentMethod || tx.method || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '12px', direction: 'ltr', textAlign: 'right' }}>
                      {tx.reference || tx.referenceNumber || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '12px', direction: 'ltr', textAlign: 'right' }}>
                      {tx.occuredAt || tx.createdAt || tx.date
                        ? new Date(tx.occuredAt || tx.createdAt || tx.date).toLocaleDateString('ar-IQ')
                        : '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WalletPage;